import React, { useState, useEffect, useRef } from 'react';
import { Camera, Mic, CheckCircle, AlertCircle, Video, Wifi, Activity } from 'lucide-react';

const HardwareCheck = ({ onComplete, onCancel }) => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [cameraPassed, setCameraPassed] = useState(false);
  const [micPassed, setMicPassed] = useState(false);
  
  // Network Latency / RTT state
  const [ping, setPing] = useState(null);
  const [pingStatus, setPingStatus] = useState('testing'); // 'testing' | 'optimal' | 'fair' | 'high' | 'unknown'

  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const micBarRef = useRef(null);

  useEffect(() => {
    let active = true;
    
    const startHardware = async () => {
      try {
        const str = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (!active) {
          str.getTracks().forEach(t => t.stop());
          return;
        }
        setStream(str);
        if (videoRef.current) {
          videoRef.current.srcObject = str;
        }
        setCameraPassed(true);

        // Setup audio analysis
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        analyserRef.current = analyser;
        
        const source = audioCtx.createMediaStreamSource(str);
        source.connect(analyser);
        
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        
        const updateMicLevel = () => {
          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for(let i=0; i<dataArray.length; i++) sum += dataArray[i];
          const avg = sum / dataArray.length;
          
          if (micBarRef.current) {
            const visualWidth = Math.min(100, avg * 3);
            micBarRef.current.style.width = `${visualWidth}%`;
          }
          
          if (avg > 10 && !micPassed) {
             setMicPassed(true);
          }
          
          animationRef.current = requestAnimationFrame(updateMicLevel);
        };
        updateMicLevel();

      } catch (err) {
        if (active) {
          setError("Microphone or Camera access denied. Please check your browser permissions.");
        }
      }
    };

    // Measure Network RTT / Ping to server
    const checkNetworkLatency = async () => {
      try {
        const samples = [];
        for (let i = 0; i < 3; i++) {
          const start = performance.now();
          await fetch('/api/public/ping', { cache: 'no-store' });
          const end = performance.now();
          samples.push(end - start);
        }
        if (!active) return;
        const avgPing = Math.round(samples.reduce((a, b) => a + b, 0) / samples.length);
        setPing(avgPing);
        if (avgPing < 120) {
          setPingStatus('optimal');
        } else if (avgPing <= 250) {
          setPingStatus('fair');
        } else {
          setPingStatus('high');
        }
      } catch (err) {
        console.error("Network ping check error:", err);
        if (active) setPingStatus('unknown');
      }
    };

    startHardware();
    checkNetworkLatency();

    return () => {
      active = false;
      cleanupMedia();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanupMedia = () => {
    if (stream) {
      try {
        stream.getTracks().forEach(t => t.stop());
      } catch (_) {}
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close().catch(() => {});
        }
      } catch (_) {}
      audioContextRef.current = null;
    }
  };

  const handleProceed = () => {
    cleanupMedia();
    onComplete();
  };

  const getPingColor = () => {
    if (pingStatus === 'optimal') return '#16a34a';
    if (pingStatus === 'fair') return '#d97706';
    if (pingStatus === 'high') return '#dc2626';
    return '#64748b';
  };

  const getPingBg = () => {
    if (pingStatus === 'optimal') return '#dcfce7';
    if (pingStatus === 'fair') return '#fef3c7';
    if (pingStatus === 'high') return '#fee2e2';
    return '#f1f5f9';
  };

  const getPingLabel = () => {
    if (pingStatus === 'testing') return 'Testing latency...';
    if (pingStatus === 'optimal') return `${ping} ms · Optimal (Low Latency)`;
    if (pingStatus === 'fair') return `${ping} ms · Fair Latency`;
    if (pingStatus === 'high') return `${ping} ms · High Latency Warning`;
    return 'Latency check failed';
  };

  return (
    <div style={{ padding: '24px', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)' }}>
      <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Video size={24} color="var(--primary)" /> System & Hardware Check
      </h3>
      <p style={{ color: 'var(--text-muted)' }}>Please ensure your camera, microphone, and network connection latency are ready before starting the live interview.</p>
      
      {error ? (
        <div style={{ background: '#fef2f2', border: '1px solid #f87171', color: '#b91c1c', padding: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={20} /> {error}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px' }}>
          <div style={{ background: '#000', borderRadius: '12px', overflow: 'hidden', position: 'relative', height: '280px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {!cameraPassed && <span style={{ color: '#fff', position: 'absolute' }}>Loading camera...</span>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
            {/* Camera Check */}
            <div style={{ padding: '14px', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ background: cameraPassed ? '#dcfce7' : '#f1f5f9', padding: '9px', borderRadius: '50%', flexShrink: 0 }}>
                <Camera size={18} color={cameraPassed ? '#16a34a' : '#64748b'} />
              </div>
              <div style={{ minWidth: 0 }}>
                <strong style={{ display: 'block', fontSize: '0.88rem' }}>Webcam</strong>
                <span style={{ fontSize: '0.8rem', color: cameraPassed ? '#16a34a' : 'var(--text-muted)' }}>
                  {cameraPassed ? 'Camera Active' : 'Waiting...'}
                </span>
              </div>
              {cameraPassed && <CheckCircle size={18} color="#16a34a" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
            </div>

            {/* Microphone Check */}
            <div style={{ padding: '14px', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ background: micPassed ? '#dcfce7' : '#f1f5f9', padding: '9px', borderRadius: '50%', flexShrink: 0 }}>
                <Mic size={18} color={micPassed ? '#16a34a' : '#64748b'} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: 'block', fontSize: '0.88rem' }}>Microphone</strong>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                  <div style={{ height: '5px', background: '#e2e8f0', borderRadius: '3px', flex: 1, overflow: 'hidden' }}>
                    <div ref={micBarRef} style={{ height: '100%', background: '#16a34a', width: '0%', transition: 'width 0.1s' }}></div>
                  </div>
                </div>
              </div>
              {micPassed && <CheckCircle size={18} color="#16a34a" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
            </div>

            {/* Network Latency RTT Check */}
            <div style={{ padding: '14px', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ background: getPingBg(), padding: '9px', borderRadius: '50%', flexShrink: 0 }}>
                <Wifi size={18} color={getPingColor()} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <strong style={{ display: 'block', fontSize: '0.88rem' }}>Network RTT</strong>
                <span style={{ fontSize: '0.78rem', color: getPingColor(), fontWeight: '600', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getPingLabel()}
                </span>
              </div>
              {pingStatus === 'optimal' && <CheckCircle size={18} color="#16a34a" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
            </div>
          </div>

          {/* High Latency Warning Notice */}
          {pingStatus === 'high' && (
            <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', color: '#d46b08', padding: '12px 14px', borderRadius: '8px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '8px', lineHeight: '1.45' }}>
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span><strong>High Network Latency Detected ({ping} ms):</strong> WebRTC real-time avatar interaction requires low network ping (&lt;150 ms). If you experience speech/response lag during the interview, consider switching to a stable Wi-Fi or Ethernet connection.</span>
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '10px' }}>
            <button onClick={onCancel} className="pop-btn-secondary" style={{ padding: '10px 20px', borderRadius: '8px', background: '#f1f5f9', border: '1px solid #cbd5e1', cursor: 'pointer' }}>Cancel</button>
            <button 
              onClick={handleProceed} 
              disabled={!cameraPassed}
              className="pop-btn-primary" 
              style={{ padding: '10px 20px', borderRadius: '8px', background: '#3b82f6', color: '#fff', border: 'none', opacity: !cameraPassed ? 0.5 : 1, cursor: cameraPassed ? 'pointer' : 'not-allowed' }}
            >
              Proceed to Interview
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HardwareCheck;
